import { useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "./Seo";
import Breadcrumbs, { buildBreadcrumbSchema } from "./Breadcrumbs";
import "./App.css";
import "./PrivacyPolicy.css";
import Footer from "./Footer";
import Navbar from "./Navbar";

const breadcrumbItems = [
  { name: 'Home', path: '/' },
  { name: 'Terms of Service', path: '/terms-of-service' },
];

const sections = [
  {
    id: "introduction",
    badge: "01",
    title: "Introduction",
    body: `Welcome to Baatcheet. Kindly go through these terms thoroughly before accessing the Baatcheet website (https://baatcheetapp.in/) and mobile application (referred to hereinafter as the "App"). Baatcheet is a voice-based platform that offers audio conversations, live rooms, and voice notes — designed to foster meaningful guidance, open expression, and a supportive community. If you are not in agreement with any portion of these terms or our privacy policy, we request that you discontinue use of our website, app, or any associated services.`,
  },
  {
    id: "acceptance",
    badge: "02",
    title: "Acceptance of Terms of Use Agreement",
    body: `We hold the right to revise these terms of use and our privacy policy whenever necessary, including to reflect new functionalities, updated business practices, or legal requirements. The most current version will always be accessible on our app and website, along with the date it was last revised. We encourage you to check these terms and the privacy policy from time to time, as all updates take effect upon being published. Your continued use of the Baatcheet app and website following any revision shall be considered as your acceptance of the updated terms. Baatcheet will inform its users of any such revisions through the App.`,
  },
  {
    id: "eligibility",
    badge: "03",
    title: "Eligibility",
    body: `Baatcheet is a voice-based platform designed to provide meaningful audio interactions, guidance, and a safe space for open conversations. By creating a Baatcheet account, you confirm the following:

— You are a minimum of 18 years of age.
— You are legally capable of entering into a binding agreement with Baatcheet.
— You are not barred from using this service under Indian law or any other applicable jurisdiction.
— You have no unspent convictions or active legal orders pertaining to assault, violence, sexual misconduct, or harassment.
— You will not access Our Website or App if you have been previously removed from Baatcheet.
— You will abide by this agreement and all relevant local, state, national, and international laws, regulations, and rules.`,
  },
  {
    id: "governing-law",
    badge: "04",
    title: "Governing Law",
    body: `These terms of use and privacy policy are subject to the laws of India. You agree to submit to the exclusive jurisdiction of the competent courts in India for any disputes arising hereunder.`,
  },
  {
    id: "registration",
    badge: "05",
    title: "Registration and Your Account",
    body: `To access the Website or App, you must complete registration and verify your identity. Upon registration, you may set up a personal account ("Your Account"). During this process, we may require your profile photograph and additional details such as your name, gender, date of birth, city, personal interests, and a brief introduction about yourself. This information helps us personalize your experience on the platform.

Your Account is linked to your registered phone number or social login. By signing in via a third-party platform, you authorize us to access relevant public profile information to help set up your Baatcheet account.

Baatcheet does not take responsibility for the login credentials used to access the service; you bear full responsibility for all activity conducted under your account.

By registering on Baatcheet, you give us permission to send you SMS notifications related to your account activity.

Should you suspect unauthorized access to your account, please write to baatcheet.connect@gmail.com from your registered email address to request account removal.`,
  },
  {
    id: "community-guidelines",
    badge: "06",
    title: "Community Guidelines",
    body: `All Baatcheet users are expected to engage respectfully and responsibly. Conduct that violates these standards may lead to termination of membership. The following behaviors are not permitted:

— Posting or sharing defamatory, abusive, obscene, offensive, sexually explicit, threatening, harassing, racially discriminatory, or illegal material.
— Using automated tools or bots to extract or reproduce content from the service.
— Using Baatcheet for personal business promotion or commercial solicitation.
— Providing false, misleading, or inaccurate information to Baatcheet or other users.
— Sharing links to external websites on your profile or in conversations.
— Disclosing your Baatcheet login credentials to any other person.
— Exercising caution when sharing personal information with other users; avoid publishing complete contact details on Your Account.
— All photographs will be reviewed prior to being displayed on Baatcheet.
— Uploading malicious software, viruses, or any code that could compromise the security of Baatcheet's platform.
— Posting content that is vulgar, defamatory, controversial, or in violation of copyright.
— Disrupting public order or interfering with the normal functioning of Baatcheet's services in any way.
— Continuing to contact any user once they have requested you to stop.
— Posting, copying, modifying, or distributing any confidential or proprietary material without prior written authorization from the rightful owner.

You agree to compensate Baatcheet for any third-party claims or damages arising from your breach or alleged breach of these terms.

When contacting our support team, refrain from any abusive, offensive, threatening, or otherwise inappropriate conduct. We reserve the right to immediately terminate your account if your behavior towards our team is found to be hostile or inappropriate.

Baatcheet reserves the right to investigate and/or terminate your account without refund if we have reason to believe you have misled the platform or its users, misused the Service, or engaged in unlawful or inappropriate conduct — including actions involving users you connected with through Baatcheet.`,
  },
  {
    id: "autopay",
    badge: "07",
    title: "AutoPay Usage Guideline",
    body: `**Nature of the Platform**

— The Platform provides communication services enabling users to interact for social, entertainment, and conversational purposes.
— The Platform does not promote, facilitate, or endorse dating or matchmaking services. All services are strictly for social interaction and entertainment.

**Purpose of Autopay**

The Autopay feature enables users to authorize automatic or recurring payments for:

— Wallet top-ups
— Subscriptions
— In-app credits
— Other approved platform services`,
  },
  {
    id: "live-features",
    badge: "08",
    title: "Live Feature Usage",
    body: `Baatcheet offers real-time live audio and/or video functionality ("Live Sessions"). By participating in any Live Session, you agree to adhere to the rules outlined here, in the Community Guidelines, and in the Privacy Policy.

**Rules for Live Sessions**

You agree that You will NOT:

— Engage in harassment, bullying, threats, abusive language, hate speech, or any behavior harmful to other participants.
— Share sexually explicit, nude, or otherwise inappropriate content.
— Record, screenshot, or capture any portion of a Live Session without the explicit consent of all participants.
— Impersonate another person or misrepresent your identity.
— Use Live Sessions for solicitation, advertising, or any commercial purpose.
— Participate in dangerous, illegal, or harmful activities during live audio or video interactions.

**Public Visibility**

When you join or host a Live Session, your profile information may be visible to all participants in the room. Your voice, camera feed, and interactions may be seen or heard by others present. By accessing this feature, you consent to this level of visibility.

**Moderation & Enforcement**

Baatcheet reserves the right to monitor activity taking place during Live Sessions, act on reported violations, remove users from live rooms, and suspend or permanently ban accounts found to be in breach of the rules.

**Recording and Monitoring Disclaimer**

Unless explicitly communicated otherwise, Live Sessions are neither recorded nor saved by Baatcheet. Baatcheet may monitor live interactions through automated systems and human review solely in response to reported misconduct. Users are strictly prohibited from recording or distributing live audio or video without the consent of all participants.

**User Responsibility**

You are solely and entirely responsible for everything you say, do, or display during a Live Session, ensuring that you do not disclose sensitive personal information, and your conduct with other users, both within and outside the App. Baatcheet holds no liability for the actions, conduct, or content of any other participant.

**Live Host Responsibilities**

If you host a Live Session, you agree to foster and maintain a safe environment for all participants, report or remove users who are found to be violating the rules, and uphold all community and safety standards at all times. Non-compliance may result in the suspension of hosting privileges or account termination.`,
  },
  {
    id: "safety",
    badge: "09",
    title: "Safety",
    body: `Baatcheet does not perform criminal or formal background checks on its users, nor does it verify the accuracy of user-submitted information. You are solely responsible for taking appropriate precautions when using the Website and App and when interacting with other users. Be aware of the inherent risks involved in online and offline interactions. We do not guarantee the accuracy or reliability of any information provided by other users. While we strive to keep the Website and App up-to-date, we do not warrant its completeness or accuracy. Any reliance on information available through the service is at your own risk. Uninterrupted or error-free access to the Website and App is not guaranteed. All content and services are provided on an "as is" and "as available" basis. Any decisions made based on information obtained through the Website and App are entirely at your own discretion and risk.

Please be aware that calls may be recorded for safety and security purposes.

Baatcheet does not collect data from individuals under the age of 18. Should a member falsely represent themselves as being 18 or older, their parent or guardian should notify us at baatcheet.connect@gmail.com to request deletion of the minor's data. Baatcheet is committed to promptly removing such data upon notification.`,
  },
  {
    id: "license",
    badge: "10",
    title: "License",
    body: `Baatcheet grants you a limited, non-transferable license for personal and private use of the platform. By using Baatcheet, you grant us a royalty-free, non-exclusive, worldwide, and perpetual license to use, reproduce, modify, translate, and display any content you submit through the App, solely for the purpose of facilitating communication with other users.

Baatcheet reserves the right to modify or discontinue the service, in whole or in part, at any time and without prior notice.`,
  },
  {
    id: "account-deletion",
    badge: "11",
    title: "Deletion of Account",
    body: `You may delete your Baatcheet account at any time without notice. If you choose to return to Baatcheet in the future, a fresh registration will be required.

Upon deletion of your account:

— Your profile will be removed from the platform.
— Deleted profiles cannot be reactivated.

Baatcheet reserves the right to terminate your membership, suspend your profile, or restrict access at any time in the event of a breach of these terms, with or without prior notice. Under no circumstances will any purchases made on Baatcheet be eligible for a refund.`,
  },
  {
    id: "copyright",
    badge: "12",
    title: "Copyright Infringement Claims",
    body: `You may not upload, distribute, or reproduce content protected by copyright, trademarks, or other proprietary rights without obtaining prior written permission from the respective owner. If you believe that your work has been reproduced on the Service in a manner that constitutes copyright infringement, please submit the following information to baatcheet.connect@gmail.com:

— A physical or electronic signature of the person authorized to act on behalf of the copyright holder.
— A description of the copyrighted work you claim has been infringed.
— The specific location within the app where the allegedly infringing material appears.
— Your full name, postal address, telephone number, and email address.
— A written declaration stating your good-faith belief that the use in question has not been authorized by the copyright owner, their agent, or the law.
— A statement, made under penalty of perjury, affirming that the information in your notice is accurate and that you are the copyright owner or are authorized to act on their behalf.`,
  },
  {
    id: "external-links",
    badge: "13",
    title: "External Links and Services",
    body: `The service may contain links to third-party websites or external resources. As Baatcheet has no authority over these sites, you acknowledge and agree that Baatcheet is not accountable for their availability or for any content, advertising, products, or materials found on or through them.`,
  },
  {
    id: "liability",
    badge: "14",
    title: "Limitation of Liability and Disputes",
    body: `You are fully responsible for your interactions with other Baatcheet users. Baatcheet may, at its discretion, monitor disputes between users but is under no obligation to do so. We do not offer arbitration or mediation services for personal or private disputes between users. Baatcheet reserves the right to take appropriate action against users found to be in violation of these terms.

Under no circumstances shall Baatcheet, its affiliates, employees, agents, licensors, or partners be held liable for any damages whatsoever — whether direct, indirect, general, special, compensatory, consequential, or incidental — arising from or related to your conduct or the conduct of others in connection with the use of the service. This includes, without limitation, bodily harm, emotional distress, loss of data, loss of income, property damage, virus-related losses, wasted time, breach of contract, or third-party claims — even where Baatcheet has been informed of the possibility of such harm.

Baatcheet's total liability arising from or related to the Website, App, or your use of our content — regardless of the basis of the claim — shall not exceed the amount you have paid for purchases on Baatcheet.`,
  },
  {
    id: "indemnity",
    badge: "15",
    title: "Indemnity",
    body: `To the maximum extent permitted under Indian law, you agree to defend, indemnify, and hold harmless Baatcheet, along with its officers, directors, and employees, from any liabilities, claims, damages, complaints, losses, costs, or expenses — including legal fees — arising from or connected to your use of the services, your content, or your violation of this agreement.`,
  },
  {
    id: "disclaimers",
    badge: "16",
    title: "Disclaimers",
    body: `Baatcheet makes no representations or warranties that:

— Any profiles or connections presented will result in compatibility or success in finding a partner.
— The service will operate without interruption, remain fully secure, or be free of errors.
— Any defects in the service will be identified and resolved.
— Any content or information accessed through the service will be accurate or reliable.
— Any feedback or suggestions submitted will be acted upon.

Baatcheet accepts no responsibility for content posted, sent, or received by you, other users, or third parties through the service. Any material downloaded or accessed through the service is done entirely at your own risk and discretion.

Baatcheet disclaims all responsibility for the conduct of any user, both on and off the platform.`,
  },
  {
    id: "miscellaneous",
    badge: "17",
    title: "Miscellaneous",
    body: `Baatcheet may be partially or wholly acquired by another entity at any time and without prior notice.

If you breach these terms and Baatcheet does not immediately act, this does not waive our right to take action in any future situation involving a breach.

Should any provision of these terms be found invalid or unenforceable by a court or regulatory authority, the remaining provisions shall continue in full effect. We will make the necessary adjustments to replace the invalid provision with one that most closely reflects its original intent.

These terms are intended solely for the benefit of you and Baatcheet and do not confer rights on any third party.

We reserve the right, at our sole discretion, to revise these terms of use and privacy policy at any time. The latest version will always be available on our App and Website. We encourage you to review these terms periodically, as all changes take effect upon publication. Your continued use of the Baatcheet App and Website following any update constitutes your acceptance of the revised terms.`,
  },
];

function TermsOfService() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.08 },
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="Terms of Service | Baatcheet"
        description="Baatcheet's Terms of Service — the rules and guidelines that govern use of our app and website."
        path="/terms-of-service"
        jsonLd={buildBreadcrumbSchema(breadcrumbItems)}
      />
      <div className="bg" />
      <div className="confetti">
        <i className="dot s1" />
        <i className="cbar s2" />
        <i className="dot s3" />
        <i className="cbar s4" />
        <i className="dot s5" />
        <i className="dot s6" />
        <i className="cbar s7" />
        <i className="dot s8" />
        <i className="cbar s9" />
        <i className="dot s10" />
      </div>

      <Navbar />
      <Breadcrumbs items={breadcrumbItems} />

      <main>
        {/* ── Hero ── */}
        <section className="pp-hero shell reveal">
          <div className="pp-hero-tag">Legal Documents</div>
          <h1 className="pp-hero-title">
            Terms of
            <br />
            <span className="pp-hero-accent">Service</span>
          </h1>
          <p className="pp-hero-sub">
            These Terms govern your access to and use of Baatcheet. By using the
            Platform, you agree to be bound by them. Please read carefully
            before proceeding.
          </p>
          <div className="pp-meta">
            <span className="pp-meta-item">Last Updated: May 2026</span>
            <span className="pp-meta-sep">·</span>
            <span className="pp-meta-item">Governed by Indian Law</span>
            <span className="pp-meta-sep">·</span>
            <a
              className="pp-meta-item pp-meta-link"
              href="mailto:baatcheet.connect@gmail.com?subject=Terms%20Query"
            >
              Questions? Email us
            </a>
          </div>
        </section>

        {/* ── Table of Contents ── */}
        <section className="pp-toc-wrap shell reveal">
          <div className="pp-toc">
            <div className="pp-toc-label">Jump to section</div>
            <div className="pp-toc-grid">
              {sections.map((s) => (
                <a key={s.id} className="pp-toc-item" href={`#${s.id}`}>
                  <span className="pp-toc-num">{s.badge}</span>
                  <span className="pp-toc-name">{s.title}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ── Terms Sections ── */}
        <section className="pp-body shell">
          {sections.map((s, i) => (
            <article
              key={s.id}
              id={s.id}
              className={`pp-article reveal ${i % 2 === 1 ? "pp-article--alt" : ""}`}
            >
              <div className="pp-article-num">{s.badge}</div>
              <div className="pp-article-content">
                <h2 className="pp-article-title">{s.title}</h2>
                <div className="pp-article-body">
                  {s.body.split("\n\n").map((para, pi) => {
                    if (
                      para.startsWith("**") &&
                      para.endsWith("**") &&
                      para.indexOf("**", 2) === para.length - 2
                    ) {
                      return (
                        <h3 key={pi} className="pp-subhead">
                          {para.slice(2, -2)}
                        </h3>
                      );
                    }
                    if (para.startsWith("— ")) {
                      const items = para.split("\n").filter(Boolean);
                      return (
                        <ul key={pi} className="pp-bullets">
                          {items.map((item, ii) => (
                            <li key={ii}>{item.replace(/^— /, "")}</li>
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p
                        key={pi}
                        dangerouslySetInnerHTML={{
                          __html: para.replace(
                            /\*\*(.+?)\*\*/g,
                            "<strong>$1</strong>",
                          ),
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </article>
          ))}
        </section>

        {/* ── CTA strip ── */}
        <section className="pp-cta shell reveal">
          <div className="pp-cta-inner">
            <div className="pp-cta-text">
              <h3>Have a legal query?</h3>
              <p>
                Our team responds to all Terms-related queries within 2–3
                business days.
              </p>
            </div>
            <a
              className="btn btn-cyan"
              href="mailto:baatcheet.connect@gmail.com?subject=Terms%20Query"
            >
              ✉ Contact Us
            </a>
          </div>
        </section>

        <div className="pp-related shell">
          <span>Related:</span>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/refund-policy">Refund Policy</Link>
          <Link to="/account-deletion">Account Deletion</Link>
          <Link to="/safety">Safety &amp; Trust</Link>
        </div>
      </main>

      <Footer />
    </>
  );
}

export default TermsOfService;
